import React, { useState, useEffect } from 'react';
import { usePersona, type AudiencePersona } from '@/contexts/persona-context';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import SearchableMultiSelect from '@/components/ui/searchable-multi-select';
import EmojiPicker from '@/components/ui/emoji-picker';
import {
  Target,
  Users,
  Calendar,
  DollarSign,
  Heart,
  MapPin,
  Music,
  Building2,
  Lock,
  Unlock,
  Trash2,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface FilterOption {
  value: string;
  label: string;
  description?: string;
}

interface PersonaFormProps {
  initialData?: AudiencePersona;
  preSelectedBrandId?: number;
  onSave: (persona: AudiencePersona) => void;
  onCancel?: () => void;
  showCancelButton?: boolean;
  showDeleteButton?: boolean;
  onDelete?: () => void;
  isLoading?: boolean;
  saveButtonText?: string;
}

// Fetch quick persona templates from database
const useQuickPersonaTemplates = () => {
  const { data: quickPersonasData = [] } = useQuery({
    queryKey: ['/api/quick-personas'],
    queryFn: async () => {
      const response = await fetch('/api/quick-personas');
      if (!response.ok) return [];
      return response.json();
    },
  });

  // Transform database personas to match the expected template format
  return quickPersonasData.map((persona: any) => ({
    name: persona.name,
    icon: persona.icon,
    data: {
      profileName: persona.name,
      description: persona.description || '',
      ageRanges: persona.ageRanges || [],
      genders: persona.genders || [],
      ethnicities: persona.ethnicities || [],
      incomeLevels: persona.incomeLevels || [],
      lifestyleAttributes: persona.lifestyleAttributes || [],
      musicGenres: persona.musicGenres || [],
      targetLocations: persona.targetLocations || [],
    },
  }));
};

// Component for field header with required badge
const FieldHeader = ({
  icon,
  label,
  field,
  selectedValues,
  formData,
  setFormData,
}: {
  icon: React.ReactNode;
  label: string;
  field: string;
  selectedValues: string[];
  formData: Partial<AudiencePersona & { icon?: string }>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<AudiencePersona & { icon?: string }>>>;
}) => {
  const requiredField = `required${field.charAt(0).toUpperCase()}${field.slice(1)}` as keyof AudiencePersona;
  const requiredValues = (formData[requiredField] as string[]) || [];

  const toggleRequired = (value: string) => {
    const currentRequired = requiredValues;
    const newRequired = currentRequired.includes(value)
      ? currentRequired.filter(v => v !== value)
      : [...currentRequired, value];

    setFormData({
      ...formData,
      [requiredField]: newRequired,
    });
  };

  return (
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center gap-2 text-sm font-medium">
        {icon}
        {label}
      </div>
      {selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedValues.map(value => {
            const isRequired = requiredValues.includes(value);
            return (
              <Button
                key={value}
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => toggleRequired(value)}
                className={cn(
                  "h-6 text-xs gap-1 px-2",
                  isRequired
                    ? "bg-orange-100 text-orange-700 hover:bg-orange-200"
                    : "text-muted-foreground hover:bg-muted"
                )}
              >
                {isRequired ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
                {value}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function PersonaForm({
  initialData,
  preSelectedBrandId,
  onSave,
  onCancel,
  showCancelButton = false,
  showDeleteButton = false,
  onDelete,
  isLoading: externalLoading = false,
  saveButtonText,
}: PersonaFormProps) {
  const { createPersona, updatePersona, savedPersonas } = usePersona();
  const PERSONA_TEMPLATES = useQuickPersonaTemplates();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [selectedBrandId, setSelectedBrandId] = useState<number | undefined>(
    initialData?.brandId || preSelectedBrandId
  );

  const isEditMode = !!initialData?.id;

  // Fetch brands for the user
  const { data: brands = [] } = useQuery({
    queryKey: ['/api/brands'],
    queryFn: async () => {
      const response = await fetch('/api/brands', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) return [];
      return response.json();
    },
  });

  // Form state
  const [formData, setFormData] = useState<Partial<AudiencePersona & { icon?: string }>>({
    profileName: initialData?.profileName || '',
    description: initialData?.description || '',
    icon: (initialData as any)?.icon || '🎯',
    ageRanges: initialData?.ageRanges || [],
    genders: initialData?.genders || [],
    ethnicities: initialData?.ethnicities || [],
    incomeLevels: initialData?.incomeLevels || [],
    targetLocations: initialData?.targetLocations || [],
    lifestyleAttributes: initialData?.lifestyleAttributes || [],
    musicGenres: initialData?.musicGenres || [],
    requiredAgeRanges: initialData?.requiredAgeRanges || [],
    requiredGenders: initialData?.requiredGenders || [],
    requiredEthnicities: initialData?.requiredEthnicities || [],
    requiredIncomeLevels: initialData?.requiredIncomeLevels || [],
    requiredTargetLocations: initialData?.requiredTargetLocations || [],
    requiredLifestyleAttributes: initialData?.requiredLifestyleAttributes || [],
    requiredMusicGenres: initialData?.requiredMusicGenres || [],
    brandId: initialData?.brandId || preSelectedBrandId,
  });

  // Update form when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      setFormData({
        profileName: initialData.profileName || '',
        description: initialData.description || '',
        icon: (initialData as any).icon || '🎯',
        ageRanges: initialData.ageRanges || [],
        genders: initialData.genders || [],
        ethnicities: initialData.ethnicities || [],
        incomeLevels: initialData.incomeLevels || [],
        targetLocations: initialData.targetLocations || [],
        lifestyleAttributes: initialData.lifestyleAttributes || [],
        musicGenres: initialData.musicGenres || [],
        requiredAgeRanges: initialData.requiredAgeRanges || [],
        requiredGenders: initialData.requiredGenders || [],
        requiredEthnicities: initialData.requiredEthnicities || [],
        requiredIncomeLevels: initialData.requiredIncomeLevels || [],
        requiredTargetLocations: initialData.requiredTargetLocations || [],
        requiredLifestyleAttributes: initialData.requiredLifestyleAttributes || [],
        requiredMusicGenres: initialData.requiredMusicGenres || [],
        brandId: initialData.brandId || preSelectedBrandId,
      });
      setSelectedBrandId(initialData.brandId || preSelectedBrandId);
    }
  }, [initialData, preSelectedBrandId]);

  // Set default brand if only one exists
  useEffect(() => {
    if (brands.length === 1 && !selectedBrandId) {
      setSelectedBrandId(brands[0].id);
      setFormData(prev => ({ ...prev, brandId: brands[0].id }));
    }
  }, [brands, selectedBrandId]);

  // Fetch dynamic data from database
  const { data: dynamicGenres = [] } = useQuery({
    queryKey: ['/api/filter-options', 'musicGenres'],
    queryFn: async () => {
      const response = await fetch('/api/filter-options?category=musicGenres');
      if (!response.ok) return [];
      return response.json();
    },
  });

  const { data: dynamicEthnicities = [] } = useQuery({
    queryKey: ['/api/filter-options', 'fanEthnicity'],
    queryFn: async () => {
      const response = await fetch('/api/filter-options?category=fanEthnicity');
      if (!response.ok) return [];
      return response.json();
    },
  });

  const { data: dynamicAttributes = [] } = useQuery({
    queryKey: ['/api/filter-options', 'fanAttributes'],
    queryFn: async () => {
      const response = await fetch('/api/filter-options?category=fanAttributes');
      if (!response.ok) return [];
      return response.json();
    },
  });

  // Fetch DMA options
  const { data: dmaOptions = [] } = useQuery({
    queryKey: ['/api/dmas'],
    queryFn: async () => {
      const response = await fetch('/api/dmas');
      if (!response.ok) return [];
      const dmas = await response.json();
      return dmas.map((dma: any) => ({
        value: dma.name,
        label: dma.name,
        description: `Rank #${dma.rank} - ${dma.states?.join(', ') || ''}`
      }));
    },
  });

  // Fetch managed filter options (admin-controlled)
  const { data: filterOptions = {} } = useQuery({
    queryKey: ['/api/filter-options-grouped'],
    queryFn: async () => {
      const response = await fetch('/api/filter-options');
      if (!response.ok) return {};

      const options = await response.json();

      // Group options by category for managed filters only
      const grouped: Record<string, FilterOption[]> = {};
      options.forEach((option: any) => {
        // Only include non-dynamic categories here
        if (!['fanAttributes', 'musicGenres', 'fanEthnicity'].includes(option.category)) {
          if (!grouped[option.category]) {
            grouped[option.category] = [];
          }
          if (option.isActive) {
            grouped[option.category].push({
              value: option.value,
              label: option.label,
              description: option.description,
            });
          }
        }
      });

      return grouped;
    },
  });

  const handleTemplateSelect = (template: typeof PERSONA_TEMPLATES[0]) => {
    setFormData({
      ...formData,
      ...template.data,
      icon: template.icon,
    });
    setActiveTab('basic');
  };

  const updateFormField = (field: keyof AudiencePersona, values: string[]) => {
    setFormData({
      ...formData,
      [field]: values,
    });
  };

  const handleSave = async () => {
    if (!formData.profileName) {
      return; // Should show validation error
    }

    setIsLoading(true);
    try {
      let persona: AudiencePersona;

      // Include the selected brand ID
      const dataToSave = {
        ...formData,
        brandId: selectedBrandId,
      };

      if (initialData?.id) {
        await updatePersona(initialData.id, dataToSave);
        await new Promise(resolve => setTimeout(resolve, 100));
        persona = { ...initialData, ...dataToSave, id: initialData.id } as AudiencePersona;
      } else {
        persona = await createPersona(dataToSave);
      }

      onSave(persona);
    } catch (error) {
      console.error('Failed to save persona:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectedCount = () => {
    let count = 0;
    if (formData.ageRanges?.length) count += formData.ageRanges.length;
    if (formData.genders?.length) count += formData.genders.length;
    if (formData.ethnicities?.length) count += formData.ethnicities.length;
    if (formData.incomeLevels?.length) count += formData.incomeLevels.length;
    if (formData.lifestyleAttributes?.length) count += formData.lifestyleAttributes.length;
    if (formData.musicGenres?.length) count += formData.musicGenres.length;
    return count;
  };

  const loading = isLoading || externalLoading;
  const buttonText = saveButtonText || (isEditMode ? 'Update Persona' : 'Create Persona');

  return (
    <div className="space-y-6">
      {/* Quick Templates (only for new personas) */}
      {!isEditMode && PERSONA_TEMPLATES.length > 0 && (
        <div>
          <div className="text-sm font-medium mb-2 block">Quick Templates</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {PERSONA_TEMPLATES.map((template: any) => (
              <Button
                key={template.name}
                variant="outline"
                size="sm"
                onClick={() => handleTemplateSelect(template)}
                className="justify-start"
              >
                <span className="mr-2">{template.icon}</span>
                <span className="truncate">{template.name}</span>
              </Button>
            ))}
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="basic">Basic Info</TabsTrigger>
          <TabsTrigger value="demographics">Demographics</TabsTrigger>
          <TabsTrigger value="interests">Interests & Lifestyle</TabsTrigger>
        </TabsList>

        <div className="min-h-[400px] mt-4">
          <TabsContent value="basic" className="space-y-4 mt-0">
            {/* Brand Selection - only show if multiple brands exist */}
            {brands.length > 1 && (
              <div>
                <div className="flex items-center gap-2 text-sm font-medium mb-2">
                  <Building2 className="h-4 w-4" />
                  Brand *
                </div>
                <Select
                  value={selectedBrandId?.toString() || ""}
                  onValueChange={(value) => {
                    const brandId = parseInt(value);
                    setSelectedBrandId(brandId);
                    setFormData({ ...formData, brandId });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a brand for this persona" />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.map((brand: any) => (
                      <SelectItem key={brand.id} value={brand.id.toString()}>
                        <div className="flex items-center gap-2">
                          <span>{brand.name}</span>
                          {brand.audienceProfiles?.length > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {brand.audienceProfiles.length} personas
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  This persona will be associated with the selected brand
                </p>
              </div>
            )}

            {/* Show current brand if only one exists */}
            {brands.length === 1 && (
              <div className="bg-muted/50 p-3 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Brand:</span>
                  <span className="font-medium">{brands[0].name}</span>
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="name">Persona Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Young Urban Professionals"
                value={formData.profileName}
                onChange={(e) => setFormData({ ...formData, profileName: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="icon">Icon</Label>
              <EmojiPicker
                value={formData.icon || ''}
                onSelect={(emoji) => setFormData({ ...formData, icon: emoji })}
                placeholder="Choose an icon for this persona"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Select an emoji to represent this persona
              </p>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe this persona..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
          </TabsContent>

          <TabsContent value="demographics" className="space-y-4 mt-0">
            {/* Age Ranges */}
            <div>
              <FieldHeader
                icon={<Calendar className="h-4 w-4" />}
                label="Age Ranges"
                field="ageRanges"
                selectedValues={formData.ageRanges || []}
                formData={formData}
                setFormData={setFormData}
              />
              <SearchableMultiSelect
                options={filterOptions.fanAge || [
                  { value: '16-20 years old', label: '16-20 Years Old' },
                  { value: '21-29 years old', label: '21-29 Years Old' },
                  { value: '30-39 years old', label: '30-39 Years Old' },
                  { value: '40-49 years old', label: '40-49 Years Old' },
                  { value: '50-59 years old', label: '50-59 Years Old' },
                  { value: '60+ years old', label: '60+ Years Old' },
                ]}
                value={formData.ageRanges || []}
                onChange={(values) => updateFormField('ageRanges', values)}
                placeholder="Select age ranges..."
                emptyText="No age ranges found"
                showSearch={false}
              />
            </div>

            {/* Gender */}
            <div>
              <FieldHeader
                icon={<Users className="h-4 w-4" />}
                label="Gender"
                field="genders"
                selectedValues={formData.genders || []}
                formData={formData}
                setFormData={setFormData}
              />
              <SearchableMultiSelect
                options={filterOptions.fanGender || [
                  { value: 'Male', label: 'Male' },
                  { value: 'Female', label: 'Female' },
                ]}
                value={formData.genders || []}
                onChange={(values) => updateFormField('genders', values)}
                placeholder="Select genders..."
                emptyText="No gender options found"
                showSearch={false}
              />
            </div>

            {/* Income Levels */}
            <div>
              <FieldHeader
                icon={<DollarSign className="h-4 w-4" />}
                label="Income Levels"
                field="incomeLevels"
                selectedValues={formData.incomeLevels || []}
                formData={formData}
                setFormData={setFormData}
              />
              <SearchableMultiSelect
                options={filterOptions.fanIncome || [
                  { value: 'Less than $30K', label: 'Less than $30K' },
                  { value: '$30K-$49K', label: '$30K-$49K' },
                  { value: '$50K-$74K', label: '$50K-$74K' },
                  { value: '$75K-$125K', label: '$75K-$125K' },
                  { value: '$125K or More', label: '$125K or More' },
                ]}
                value={formData.incomeLevels || []}
                onChange={(values) => updateFormField('incomeLevels', values)}
                placeholder="Select income levels..."
                emptyText="No income options found"
                showSearch={false}
              />
            </div>

            {/* Ethnicity */}
            <div>
              <FieldHeader
                icon={<Users className="h-4 w-4" />}
                label="Ethnicity"
                field="ethnicities"
                selectedValues={formData.ethnicities || []}
                formData={formData}
                setFormData={setFormData}
              />
              <SearchableMultiSelect
                options={dynamicEthnicities}
                value={formData.ethnicities || []}
                onChange={(values) => updateFormField('ethnicities', values)}
                placeholder="Select ethnicities..."
                emptyText="No ethnicity options found"
                showSearch={false}
              />
            </div>

            {/* Target DMAs */}
            <div>
              <FieldHeader
                icon={<MapPin className="h-4 w-4" />}
                label="Target Markets (DMAs)"
                field="targetLocations"
                selectedValues={formData.targetLocations || []}
                formData={formData}
                setFormData={setFormData}
              />
              <SearchableMultiSelect
                variant="modal"
                modalTitle="Select Target Markets"
                options={dmaOptions}
                value={formData.targetLocations || []}
                onChange={(values) => updateFormField('targetLocations', values)}
                placeholder="Select target markets..."
                emptyText="No DMA markets found"
              />
            </div>
          </TabsContent>

          <TabsContent value="interests" className="space-y-4 mt-0">
            {/* Lifestyle Attributes */}
            <div>
              <FieldHeader
                icon={<Heart className="h-4 w-4" />}
                label="Lifestyle Attributes"
                field="lifestyleAttributes"
                selectedValues={formData.lifestyleAttributes || []}
                formData={formData}
                setFormData={setFormData}
              />
              <SearchableMultiSelect
                variant="modal"
                modalTitle="Select Lifestyle Attributes"
                options={dynamicAttributes}
                value={formData.lifestyleAttributes || []}
                onChange={(values) => updateFormField('lifestyleAttributes', values)}
                placeholder="Select lifestyle attributes..."
                emptyText="No lifestyle attributes found"
              />
            </div>

            {/* Music Genres */}
            <div>
              <FieldHeader
                icon={<Music className="h-4 w-4" />}
                label="Preferred Music Genres"
                field="musicGenres"
                selectedValues={formData.musicGenres || []}
                formData={formData}
                setFormData={setFormData}
              />
              <SearchableMultiSelect
                variant="modal"
                modalTitle="Select Music Genres"
                options={dynamicGenres}
                value={formData.musicGenres || []}
                onChange={(values) => updateFormField('musicGenres', values)}
                placeholder="Select music genres..."
                emptyText="No music genres found"
              />
            </div>
          </TabsContent>
        </div>
      </Tabs>

      {/* Selected Attributes Summary */}
      {getSelectedCount() > 0 && (
        <div className="bg-muted p-3 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              {getSelectedCount()} attributes selected
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {formData.ageRanges?.map((item) => (
              <Badge key={item} variant="secondary" className="text-xs">
                {item}
              </Badge>
            ))}
            {formData.genders?.map((item) => (
              <Badge key={item} variant="secondary" className="text-xs">
                {item}
              </Badge>
            ))}
            {formData.ethnicities?.map((item) => (
              <Badge key={item} variant="secondary" className="text-xs">
                {item}
              </Badge>
            ))}
            {formData.incomeLevels?.map((item) => (
              <Badge key={item} variant="secondary" className="text-xs">
                {item}
              </Badge>
            ))}
            {formData.lifestyleAttributes?.map((item) => (
              <Badge key={item} variant="secondary" className="text-xs">
                {item}
              </Badge>
            ))}
            {formData.musicGenres?.map((item) => (
              <Badge key={item} variant="secondary" className="text-xs">
                {item}
              </Badge>
            ))}
            {formData.targetLocations?.map((item) => (
              <Badge key={item} variant="outline" className="text-xs border-blue-300 text-blue-700">
                {item}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div>
          {showDeleteButton && onDelete && (
            <Button
              variant="destructive"
              onClick={onDelete}
              disabled={loading}
              size="sm"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          {showCancelButton && onCancel && (
            <Button variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={
              !formData.profileName ||
              loading ||
              (brands.length > 1 && !selectedBrandId)
            }
          >
            {loading ? 'Saving...' : buttonText}
          </Button>
        </div>
      </div>
    </div>
  );
}
